import api from '../../utils/api';
import {
  ADMIN_SUBSCRIPTION_TRACKER,
  ADMIN_SUBSCRIPTION_TRACKER_TEAM_SUMMARIES,
  ADMIN_TEAM_SUBSCRIPTION_TRACKER,
  ADMIN_TEAM_SUBSCRIPTION_DETAILS,
  ADMIN_USER_SUBSCRIPTION_DETAILS,
  ADMIN_CONFERENCE_SUBSCRIPTION_TRACKER,
  ADMIN_CONFERENCE_SUBSCRIPTION_TEAMS,
} from '../../constants/routes';
import {
  ConferenceSubscriptionTeamsParams,
  ConferenceSubscriptionTeamsResponse,
  ConferenceSubscriptionTrackerListResponse,
  ConferenceSubscriptionTrackerParams,
  SubscriptionTrackerListResponse,
  SubscriptionTrackerParams,
  TeamSubscriptionSummariesResponse,
  TeamSubscriptionDetailResponse,
  TeamSubscriptionTrackerListResponse,
  TeamSubscriptionTrackerParams,
  TeamSubscriptionUsersParams,
  TeamUserSubscriptionListResponse,
  UserSubscriptionDetailResponse,
} from '../../types/admin/subscriptionTracker';

type ApiResult<T> = { data: T | null; status: number; error?: string; aborted?: boolean };

export function isRequestAborted(error: unknown): boolean {
  const err = error as { code?: string; name?: string; message?: string };
  return (
    err?.code === 'ERR_CANCELED' ||
    err?.name === 'CanceledError' ||
    err?.name === 'AbortError' ||
    (typeof err?.message === 'string' && /cancel/i.test(err.message))
  );
}

function parseApiError(error: unknown, fallback: string): ApiResult<never> {
  if (isRequestAborted(error)) {
    return { data: null, status: 0, aborted: true };
  }
  const err = error as { response?: { status?: number; data?: { detail?: string; message?: string } } };
  const status = err.response?.status ?? 500;
  const detail = err.response?.data?.detail ?? err.response?.data?.message;
  return {
    data: null,
    status,
    error: typeof detail === 'string' ? detail : fallback,
  };
}

function buildUserListParams(params: SubscriptionTrackerParams): Record<string, string | number> {
  const query: Record<string, string | number> = {
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
  };
  if (params.search?.trim()) query.search = params.search.trim();
  if (params.team_id != null) query.team_id = params.team_id;
  if (params.includeTeamSummaries) query.include_team_summaries = 'true';
  if (params.trial_status) query.trial_status = params.trial_status;
  if (params.trial_end_from) query.trial_end_from = params.trial_end_from;
  if (params.trial_end_to) query.trial_end_to = params.trial_end_to;
  if (params.subscription_status) query.subscription_status = params.subscription_status;
  if (params.subscription_end_from) query.subscription_end_from = params.subscription_end_from;
  if (params.subscription_end_to) query.subscription_end_to = params.subscription_end_to;
  if (params.sport_code?.trim()) query.sport_code = params.sport_code.trim().toUpperCase();
  return query;
}

function buildTeamListParams(params: TeamSubscriptionTrackerParams): Record<string, string | number> {
  const query: Record<string, string | number> = {};
  if (params.search?.trim()) query.search = params.search.trim();
  query.limit = params.limit ?? 50;
  query.offset = params.offset ?? 0;
  if (params.subscription_status) query.subscription_status = params.subscription_status;
  if (params.subscription_start_from) query.subscription_start_from = params.subscription_start_from;
  if (params.subscription_start_to) query.subscription_start_to = params.subscription_start_to;
  if (params.subscription_end_from) query.subscription_end_from = params.subscription_end_from;
  if (params.subscription_end_to) query.subscription_end_to = params.subscription_end_to;
  if (params.sport_code?.trim()) query.sport_code = params.sport_code.trim().toUpperCase();
  return query;
}

export async function fetchSubscriptionTracker(
  params: SubscriptionTrackerParams,
  signal?: AbortSignal
): Promise<ApiResult<SubscriptionTrackerListResponse>> {
  try {
    const response = await api.get<SubscriptionTrackerListResponse>(ADMIN_SUBSCRIPTION_TRACKER, {
      params: buildUserListParams(params),
      signal,
    });
    return { data: response.data, status: response.status };
  } catch (error: unknown) {
    return parseApiError(error, 'Failed to load subscription tracker');
  }
}

export type TeamSubscriptionSummariesOptions = {
  includeActivatedBy?: boolean;
  signal?: AbortSignal;
};

export async function fetchTeamSubscriptionSummaries(
  teamIds: number[],
  options?: TeamSubscriptionSummariesOptions
): Promise<ApiResult<TeamSubscriptionSummariesResponse>> {
  const ids = [...new Set(teamIds.filter((id) => Number.isFinite(id) && id > 0))].slice(0, 100);
  if (ids.length === 0) {
    return { data: { team_summaries: {} }, status: 200 };
  }
  const signal = options?.signal;
  try {
    const response = await api.get<TeamSubscriptionSummariesResponse>(
      ADMIN_SUBSCRIPTION_TRACKER_TEAM_SUMMARIES,
      {
        params: {
          team_ids: ids,
          ...(options?.includeActivatedBy ? { include_activated_by: true } : {}),
        },
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          const rawIds = params.team_ids as number[] | undefined;
          if (Array.isArray(rawIds)) {
            rawIds.forEach((id) => searchParams.append('team_ids', String(id)));
          }
          return searchParams.toString();
        },
        signal,
      }
    );
    return { data: response.data, status: response.status };
  } catch (error: unknown) {
    return parseApiError(error, 'Failed to load team subscription summaries');
  }
}

/** Unique team ids in first-seen order (matches User Sub table grouping). */
export function orderedUniqueTeamIds(users: { team_id: number }[]): number[] {
  const seen = new Set<number>();
  const ordered: number[] = [];
  for (const user of users) {
    const tid = user.team_id;
    if (!Number.isFinite(tid) || tid <= 0 || seen.has(tid)) continue;
    seen.add(tid);
    ordered.push(tid);
  }
  return ordered;
}

export async function fetchUserSubscriptionDetails(
  userId: number,
  options?: {
    allow_all?: boolean;
    team_id?: number;
    includeActivatedBy?: boolean;
    signal?: AbortSignal;
  }
): Promise<ApiResult<UserSubscriptionDetailResponse>> {
  try {
    const params: Record<string, string | number | boolean> = {};
    if (options?.allow_all) params.allow_all = true;
    if (options?.team_id != null) params.team_id = options.team_id;
    if (options?.includeActivatedBy) params.include_activated_by = true;

    const response = await api.get<UserSubscriptionDetailResponse>(
      `${ADMIN_USER_SUBSCRIPTION_DETAILS}/${userId}/subscription-details`,
      { params, signal: options?.signal }
    );
    return { data: response.data, status: response.status };
  } catch (error: unknown) {
    return parseApiError(error, 'Failed to load subscription details');
  }
}

export async function fetchTeamSubscriptionTracker(
  params: TeamSubscriptionTrackerParams,
  signal?: AbortSignal
): Promise<ApiResult<TeamSubscriptionTrackerListResponse>> {
  try {
    const response = await api.get<TeamSubscriptionTrackerListResponse>(ADMIN_TEAM_SUBSCRIPTION_TRACKER, {
      params: buildTeamListParams(params),
      signal,
    });
    return { data: response.data, status: response.status };
  } catch (error: unknown) {
    return parseApiError(error, 'Failed to load team subscription tracker');
  }
}

export async function fetchTeamSubscriptionDetails(
  teamId: number,
  signal?: AbortSignal
): Promise<ApiResult<TeamSubscriptionDetailResponse>> {
  try {
    const response = await api.get<TeamSubscriptionDetailResponse>(
      `${ADMIN_TEAM_SUBSCRIPTION_DETAILS}/${teamId}/subscription-details`,
      { signal }
    );
    return { data: response.data, status: response.status };
  } catch (error: unknown) {
    return parseApiError(error, 'Failed to load team subscription details');
  }
}

export async function fetchTeamSubscriptionUsers(
  teamId: number,
  params: TeamSubscriptionUsersParams = {},
  signal?: AbortSignal
): Promise<ApiResult<TeamUserSubscriptionListResponse>> {
  try {
    const query: Record<string, string | number> = {
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    };
    if (params.search?.trim()) query.search = params.search.trim();

    const response = await api.get<TeamUserSubscriptionListResponse>(
      `${ADMIN_TEAM_SUBSCRIPTION_DETAILS}/${teamId}/subscription-users`,
      { params: query, signal }
    );
    return { data: response.data, status: response.status };
  } catch (error: unknown) {
    return parseApiError(error, 'Failed to load team users');
  }
}

export async function fetchConferenceSubscriptionTracker(
  params: ConferenceSubscriptionTrackerParams,
  signal?: AbortSignal
): Promise<ApiResult<ConferenceSubscriptionTrackerListResponse>> {
  try {
    const query: Record<string, string | number> = {
      sport_code: params.sport_code,
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    };
    if (params.search?.trim()) query.search = params.search.trim();

    const response = await api.get<ConferenceSubscriptionTrackerListResponse>(
      ADMIN_CONFERENCE_SUBSCRIPTION_TRACKER,
      { params: query, signal }
    );
    return { data: response.data, status: response.status };
  } catch (error: unknown) {
    return parseApiError(error, 'Failed to load conference subscription tracker');
  }
}

export async function fetchConferenceSubscriptionTeams(
  params: ConferenceSubscriptionTeamsParams,
  signal?: AbortSignal
): Promise<ApiResult<ConferenceSubscriptionTeamsResponse>> {
  try {
    const response = await api.get<ConferenceSubscriptionTeamsResponse>(
      ADMIN_CONFERENCE_SUBSCRIPTION_TEAMS,
      {
        params: {
          sport_code: params.sport_code,
          conference_name: params.conference_name,
        },
        signal,
      }
    );
    return { data: response.data, status: response.status };
  } catch (error: unknown) {
    return parseApiError(error, 'Failed to load conference teams');
  }
}
